import gql from 'graphql-tag';
import React from 'react';
import { useMutation, useQuery, MutationFunction } from 'react-apollo';
import { Button } from '../../components/Bootstrap';
import { Navigation, LayoutContent, Layout, LoadingContent } from '../../components';
import { Copy } from '../../constants';
import { Create, CreateVariables } from './__generated__/Create';
import { Delete, DeleteVariables } from './__generated__/Delete';
import {
  FufillmentCentersQuery,
  FufillmentCentersQueryVariables,
  FufillmentCentersQuery_fulfillmentCenters as FufillmentCenter,
} from './__generated__/FufillmentCentersQuery';
import { Update, UpdateVariables } from './__generated__/Update';
import { UpdatePauseStatus, UpdatePauseStatusVariables } from './__generated__/UpdatePauseStatus';
import { FulfillmentCenterTile } from './FulfillmentCenterTile';
import { ManagerModal } from './ManagerModal';
import { CenterModel } from './types';

import './FulfillmentCenterList.scss';
import { QRCodeModal } from './QRCodeModal';

const FulfillmentStation = require('./fulfillment-station.svg');

const EMPTY_CONTENT =
  'Lets add your first fulfillment station. This is where customers will have orders fulfilled, ' +
  'picked-up, or delivered from.';

const NEW_CENTER_DATA: CenterModel = {
  id: null,
  name: '',
  deliveryConfigurationId: null,
  menuId: null,
  options: {
    pickup: true,
    delivery: true,
  },
  deliveryText: Copy.deliveryTextDefault,
};

type FulfillmentCentersProps = {
  data?: FufillmentCentersQuery;
  loading: boolean;
  error?: Error;
  venueId: string;
  refetch({ variables }: { variables: FufillmentCentersQueryVariables });
  createFulfillmentCenter: MutationFunction<Create, CreateVariables>;
  updateFulfillmentCenter: MutationFunction<Update, UpdateVariables>;
  deleteFulfillmentCenter: MutationFunction<Delete, DeleteVariables>;
  updatePauseStatus: MutationFunction<UpdatePauseStatus, UpdatePauseStatusVariables>;
};

type FulfillmentCentersState = {
  modalOpen: boolean;
  currentCenterData: CenterModel | null;
  centerSelectedForQrCode: FufillmentCenter | null;
};

class FulfillmentCentersUnwrapped extends React.Component<FulfillmentCentersProps, FulfillmentCentersState> {
  constructor(props) {
    super(props);

    this.state = {
      modalOpen: false,
      currentCenterData: null,
      centerSelectedForQrCode: null,
    };
  }

  toggleModal = () => {
    this.setState({ modalOpen: !this.state.modalOpen });
  };

  add = () => {
    const { data } = this.props;

    this.setState({
      modalOpen: true,
      currentCenterData: {
        ...NEW_CENTER_DATA,
        menuId: data.menus && data.menus.length ? data.menus[0].id : null,
      },
    });
  };

  edit = (fulfillmentCenter: FufillmentCenter) => {
    const { data } = this.props;

    const defaultMenuId = data.menus?.length ? data.menus[0].id : null;
    const defaultConfigurationId = data.deliveryConfigurations?.length ? data.deliveryConfigurations[0].id : null;

    this.setState({
      modalOpen: true,
      currentCenterData: {
        id: fulfillmentCenter.id,
        name: fulfillmentCenter.name,
        menuId: fulfillmentCenter.menuId ?? defaultMenuId,
        deliveryConfigurationId: fulfillmentCenter.deliveryConfigurationId ?? defaultConfigurationId,
        options: {
          pickup: fulfillmentCenter.pickup,
          delivery: fulfillmentCenter.delivery,
        },
        deliveryText: fulfillmentCenter.deliveryText,
      },
    });
  };

  cancelGenerateQr = () => {
    this.setState({ centerSelectedForQrCode: null });
  };

  onClickGenerateQR = (centerSelectedForQrCode: FufillmentCenter) => {
    this.setState({ centerSelectedForQrCode });
  };

  deleteFulfillmentCenter = (fulfillmentCenter: FufillmentCenter) => {
    this.props.deleteFulfillmentCenter({
      variables: {
        venueId: this.props.venueId,
        fulfillmentCenterId: fulfillmentCenter.id,
      },
    });
  };

  onSubmitModal = (data: CenterModel) => {
    const saveData: CreateVariables = {
      venueId: this.props.venueId,
      center: {
        name: data.name,
        menuId: data.menuId,
        deliveryConfigurationId: data.deliveryConfigurationId,
        pickup: data.options.pickup,
        delivery: data.options.delivery,
        deliveryText: data.deliveryText,
      },
    };

    this.setState({ modalOpen: false }, async () => {
      try {
        if (data.id === null) {
          await this.props.createFulfillmentCenter({ variables: saveData });
        } else {
          await this.props.updateFulfillmentCenter({
            variables: {
              ...saveData,
              fulfillmentCenterId: data.id,
            },
          });
        }
      } catch {}
    });
  };

  renderContent() {
    if (this.props.loading || !this.props.data) {
      return <LoadingContent />;
    } else if (this.props.data.fulfillmentCenters.length === 0) {
      return (
        <div className='d-flex flex-column h-100 align-items-center justify-content-center fulfillment-centers-empty'>
          <img src={FulfillmentStation} />
          <p className='mt-4 mb-4 text-center'>{EMPTY_CONTENT}</p>
          <Button type='button' color='primary' className='btn--rounded ps-4 pe-4' onClick={this.add}>
            Add fulfillment station
          </Button>
        </div>
      );
    } else {
      const { fulfillmentCenters } = this.props.data;

      return (
        <div>
          <div className='fulfillment-centers-container'>
            {fulfillmentCenters.map((center) => (
              <FulfillmentCenterTile
                fulfillmentCenter={center}
                key={center.id}
                onEdit={this.edit}
                updatePauseStatus={this.props.updatePauseStatus}
                updateFulfillmentCenter={this.props.updateFulfillmentCenter}
                onDelete={this.deleteFulfillmentCenter}
                venueId={this.props.venueId}
                onClickGenerateQR={this.onClickGenerateQR}
              />
            ))}
          </div>
        </div>
      );
    }
  }

  render() {
    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>Fulfillment Stations</Layout.TopBar.Title>
          </Layout.TopBar.Left>
          <Layout.TopBar.Right>
            <div className='h-100 align-items-center d-none d-md-flex'>
              <Button type='button' color='primary' onClick={this.add}>
                Add fulfillment station
              </Button>
            </div>
          </Layout.TopBar.Right>
        </Layout.TopBar>
        <LayoutContent error={this.props.error} className='fulfillment-center-container'>
          <h3 className='fulfillment-centers-title'>All stations</h3>
          <div className='full-width fulfillment-center-list'>{this.renderContent()}</div>
          {!!(this.props.data && this.props.data.menus) && (
            <ManagerModal
              open={this.state.modalOpen}
              onCancel={this.toggleModal}
              initilalData={this.state.currentCenterData}
              onSubmit={this.onSubmitModal}
              key={this.state.currentCenterData?.id ?? 'create'}
              venueId={this.props.venueId}
              menus={this.props.data.menus}
              deliveryConfigurations={this.props.data.deliveryConfigurations}
            />
          )}
          {this.props.data && this.state.centerSelectedForQrCode && (
            <QRCodeModal
              open={true}
              onCancel={this.cancelGenerateQr}
              onSubmit={this.onSubmitModal}
              key={this.state.currentCenterData?.id ?? 'create-qr-code'}
              venueId={this.props.venueId}
              deliveryConfigurations={this.props.data.deliveryConfigurations}
              fulfillmentCenter={this.state.centerSelectedForQrCode}
            />
          )}
        </LayoutContent>
      </Layout>
    );
  }
}

const query = gql`
  query FufillmentCentersQuery($venueId: ID!) {
    fulfillmentCenters(venueId: $venueId) {
      delivery
      id
      name
      pickup
      deliveryText
      menuId
      deliveryConfigurationId
      allowImmediateOrders
      isOpen
      isPaused
      advancedDeliverySetup
      disabled
    }
    menus(venueId: $venueId) {
      id
      name
    }
    deliveryConfigurations(venueId: $venueId) {
      id
      name
      description
      deliveryFields {
        key
        label
      }
    }
  }
`;

const createMutation = gql`
  mutation Create($venueId: ID!, $center: FulfillmentCenterInfo!) {
    createFulfillmentCenter(venueId: $venueId, center: $center) {
      id
    }
  }
`;

const updateMutation = gql`
  mutation Update($venueId: ID!, $fulfillmentCenterId: ID!, $center: FulfillmentCenterInfo!) {
    updateFulfillmentCenter(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId, center: $center) {
      id
    }
  }
`;

const deleteMutation = gql`
  mutation Delete($venueId: ID!, $fulfillmentCenterId: ID!) {
    deleteFulfillmentCenter(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId)
  }
`;

const updatePauseStatusMutation = gql`
  mutation UpdatePauseStatus($venueId: ID!, $fulfillmentCenterId: ID!, $isPaused: Boolean!) {
    pauseFulfillmentCenter(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId, isPaused: $isPaused)
  }
`;

export const FulfillmentCenters = (props: any) => {
  const { venueId } = props.match.params;
  const { loading, error, data, refetch } = useQuery<FufillmentCentersQuery, FufillmentCentersQueryVariables>(query, {
    variables: { venueId },
    notifyOnNetworkStatusChange: true,
  });

  const [createFulfillmentCenter, createData] = useMutation<Create, CreateVariables>(createMutation, {
    onCompleted() {
      refetch();
    },
  });

  const [updateFulfillmentCenter, updateData] = useMutation<Update, UpdateVariables>(updateMutation, {
    onCompleted() {
      refetch();
    },
  });

  const [deleteFulfillmentCenter, deleteData] = useMutation<Delete, DeleteVariables>(deleteMutation, {
    onCompleted() {
      refetch();
    },
  });

  const [updatePauseStatus, updatePauseStatusData] = useMutation<UpdatePauseStatus, UpdatePauseStatusVariables>(
    updatePauseStatusMutation,
    {
      onCompleted() {
        refetch();
      },
    }
  );

  return (
    <FulfillmentCentersUnwrapped
      {...props}
      loading={
        loading || createData.loading || updateData.loading || deleteData.loading || updatePauseStatusData.loading
      }
      error={error ?? createData.error ?? updateData.error ?? deleteData.error ?? updatePauseStatusData.error}
      data={data}
      venueId={venueId}
      createFulfillmentCenter={createFulfillmentCenter}
      updateFulfillmentCenter={updateFulfillmentCenter}
      deleteFulfillmentCenter={deleteFulfillmentCenter}
      updatePauseStatus={updatePauseStatus}
      refetch={refetch}
    />
  );
};
